import { localizedPath } from '../i18n/paths'
import { useLocale } from '../i18n/LocaleProvider'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { ArrowRight, X } from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'
import { submitWebsiteInquiry } from '../services/inquiryService'
import { SiteButton } from './SiteButton'
import './InquirySheet.css'

type InquiryPrefill = {
  email?: string
  message?: string
}

type InquiryContextValue = {
  openInquiry: (prefill?: InquiryPrefill) => void
}

const InquiryContext = createContext<InquiryContextValue | null>(null)
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const EMPTY_FORM = {
  name: '',
  email: '',
  company: '',
  website: '',
  message: '',
  middleName: '',
}

const SHEET_SLIDE_DURATION_MS = 600
const SEND_AWAY_DURATION_MS = 560

const motionDuration = (duration: number) => (
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : duration
)

export function useInquiry() {
  const context = useContext(InquiryContext)
  if (!context) throw new Error('useInquiry must be used within InquiryProvider')
  return context
}

export function InquiryProvider({ children }: { children: ReactNode }) {
  const { locale, t } = useLocale()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [sendingAway, setSendingAway] = useState(false)
  const [error, setError] = useState('')
  const dialogRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const openInquiry = useCallback((prefill: InquiryPrefill = {}) => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    setForm({
      ...EMPTY_FORM,
      email: prefill.email ?? '',
      message: prefill.message ?? '',
    })
    setSubmitted(false)
    setSendingAway(false)
    setError('')
    setOpen(true)
  }, [])

  const closeInquiry = useCallback(() => {
    if (submitting) return
    setOpen(false)
    window.setTimeout(
      () => returnFocusRef.current?.focus({ preventScroll: true }),
      motionDuration(SHEET_SLIDE_DURATION_MS),
    )
  }, [submitting])

  useIsomorphicLayoutEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeInquiry()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href]',
      ))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [closeInquiry, open])

  useEffect(() => {
    if (!open) return
    const focusTimer = window.setTimeout(
      () => firstFieldRef.current?.focus({ preventScroll: true }),
      motionDuration(SHEET_SLIDE_DURATION_MS),
    )
    return () => window.clearTimeout(focusTimer)
  }, [open])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')

    // Quietly absorb automated submissions that fill the hidden field.
    if (form.middleName) {
      setSubmitted(true)
      return
    }

    setSubmitting(true)
    try {
      await submitWebsiteInquiry({
        name: form.name,
        email: form.email,
        company: form.company,
        website: form.website,
        message: form.message,
        sourcePath: `${location.pathname}${location.search}`,
      })

      setSendingAway(true)
      await new Promise(resolve => window.setTimeout(
        resolve,
        motionDuration(SEND_AWAY_DURATION_MS),
      ))
      setSubmitted(true)
      setSendingAway(false)
    } catch {
      setError(t("We couldn’t send your inquiry. Please try again, or email hello@bybeit.app."))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <InquiryContext.Provider value={{ openInquiry }}>
      <div className={`site-content${open ? ' inquiry-is-open' : ''}`} aria-hidden={open || undefined}>
        {children}
      </div>

      <div
        className={`inquiry-layer${open ? ' is-open' : ''}`}
        aria-hidden={!open}
        onMouseDown={event => {
          if (event.target === event.currentTarget) closeInquiry()
        }}
      >
        <div
          ref={dialogRef}
          className={`inquiry-sheet${sendingAway ? ' is-sending-away' : ''}${submitted ? ' is-success' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="inquiry-title"
          aria-describedby={submitted ? 'inquiry-description' : undefined}
        >
          <button
            type="button"
            className="inquiry-close"
            aria-label={t("Close inquiry form")}
            onClick={closeInquiry}
            disabled={submitting}
          >
            <X size={18} weight="bold" />
          </button>

          {submitted ? (
            <div className="inquiry-success" role="status">
              <h2 id="inquiry-title">{t('Thank you, {name}.', { name: form.name })}</h2>
              <p id="inquiry-description">
                {t('Your note is with our team. We’ll review it and get back to you at {email}.', { email: form.email })}
              </p>
              <SiteButton className="inquiry-submit" onClick={closeInquiry}> {t("Done")} </SiteButton>
            </div>
          ) : (
            <>
              <div className="inquiry-heading">
                <h2 id="inquiry-title">{t("Tell us about your Japan plans.")}</h2>
              </div>

              <form className="inquiry-form" onSubmit={handleSubmit}>
                <div className="inquiry-grid">
                  <label className="inquiry-field">
                    <span>{t("Name")}</span>
                    <input
                      ref={firstFieldRef}
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                      placeholder={t("Maya Chen")}
                      maxLength={120}
                      required
                    />
                  </label>
                  <label className="inquiry-field">
                    <span>{t("Work email")}</span>
                    <input
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={event => setForm(current => ({ ...current, email: event.target.value }))}
                      placeholder="maya@northstarlabs.com"
                      maxLength={254}
                      required
                    />
                  </label>
                  <label className="inquiry-field">
                    <span>{t("Company name")}</span>
                    <input
                      type="text"
                      autoComplete="organization"
                      value={form.company}
                      onChange={event => setForm(current => ({ ...current, company: event.target.value }))}
                      placeholder="Northstar Labs"
                      maxLength={160}
                      required
                    />
                  </label>
                  <label className="inquiry-field">
                    <span>{t("Website")} <em>{t("Optional")}</em></span>
                    <input
                      type="url"
                      autoComplete="url"
                      value={form.website}
                      onChange={event => setForm(current => ({ ...current, website: event.target.value }))}
                      placeholder="https://northstarlabs.com"
                      maxLength={500}
                    />
                  </label>
                </div>

                <label className="inquiry-field inquiry-field--message">
                  <span>{t("How can we help?")}</span>
                  <textarea
                    value={form.message}
                    onChange={event => setForm(current => ({ ...current, message: event.target.value }))}
                    placeholder={t("We’re launching our B2B SaaS product in Japan and need help with localization, pricing, and local partnerships.")}
                    maxLength={2000}
                    required
                  />
                </label>

                <label className="inquiry-honeypot" aria-hidden="true">
                  <span>{t("Middle name")}</span>
                  <input
                    type="text"
                    name="middle-name"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.middleName}
                    onChange={event => setForm(current => ({ ...current, middleName: event.target.value }))}
                  />
                </label>

                {error && <p className="inquiry-error" role="alert">{error}</p>}

                <div className="inquiry-footer">
                  <p>
                    {locale === 'ja' ? <><a href={localizedPath("/privacy", locale)} target="_blank">プライバシーポリシー</a>に同意のうえ、送信してください。</> : <>By sending this form, you agree to our <a href={localizedPath("/privacy", locale)} target="_blank">{t("Privacy Policy")}</a>.</>}
                  </p>
                  <SiteButton type="submit" className="inquiry-submit" disabled={submitting}>
                    {submitting ? t("Sending…") : t("Send inquiry")}
                    {!submitting && <ArrowRight size={16} weight="bold" />}
                  </SiteButton>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </InquiryContext.Provider>
  )
}
